import { Calendar, BookOpen, MessageSquare, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useVentureSupportHook } from "../../hooks/useVentureHooks";
import "./support.css";

export default function Support() {
  const { user, token } = useAuth();
  const isVenture = user?.role === ROLES.VENTURE;

  const { data: supportApiData, isLoading } = useVentureSupportHook({
    token,
    userId: user?.id,
    enabled: isVenture,
  });

  const supportData = supportApiData ?? {
    upcoming_check_ins: [],
    program_resources: [],
    coaching_notes: [],
    contact_details: {
      message: "",
      email: "",
      phone: "",
    },
  };

  return (
    <div className="ventureSupport">
      <header className="ventureSupport-header">
        <h1 className="ventureSupport-title">Support & Resources</h1>
        <p className="ventureSupport-subtitle">
          Coaching notes, schedules, and program resources.
        </p>
      </header>

      {isLoading && isVenture ? (
        <p className="ventureSupport-subtitle">Loading support data...</p>
      ) : null}

      <div className="ventureSupport-grid-2">
        <Card icon={Calendar} title="Upcoming Check-ins">
          {(supportData.upcoming_check_ins ?? []).length === 0 ? (
            <p className="ventureSupport-empty">No check-ins scheduled yet.</p>
          ) : (
            (supportData.upcoming_check_ins ?? []).map((item) => (
              <div key={item.id} className="ventureSupport-listItem">
                <p className="ventureSupport-listItem-title">{item.title}</p>
                <p className="ventureSupport-listItem-meta">
                  {item.date} at {item.time}
                </p>
                <p className="ventureSupport-listItem-meta">
                  With: {item.with}
                </p>
              </div>
            ))
          )}
        </Card>

        <Card icon={BookOpen} title="Program Resources">
          {(supportData.program_resources ?? []).length === 0 ? (
            <p className="ventureSupport-empty">No resources available yet.</p>
          ) : (
            (supportData.program_resources ?? []).map((resource) => (
              <a
                key={resource.id}
                href={resource.link}
                target="_blank"
                rel="noreferrer"
                className="ventureSupport-resource"
              >
                <span className="ventureSupport-resource-title">
                  {resource.name}
                  <span className="ventureSupport-resource-type">
                    {resource.type}
                  </span>
                </span>
                <ExternalLink
                  className="ventureSupport-icon-sm"
                  aria-hidden="true"
                />
              </a>
            ))
          )}
        </Card>
      </div>

      <Card
        icon={MessageSquare}
        title="Coaching Notes"
        description="Shared notes from your program manager."
      >
        {(supportData.coaching_notes ?? []).length === 0 ? (
          <p className="ventureSupport-empty">No coaching notes yet.</p>
        ) : (
          (supportData.coaching_notes ?? []).map((note) => (
            <div key={note.id} className="ventureSupport-note">
              <p className="ventureSupport-note-date">{note.date}</p>
              <p className="ventureSupport-note-body">{note.note}</p>
            </div>
          ))
        )}
      </Card>

      <Card title="Need Help?">
        <p className="ventureSupport-contact-lead">
          {supportData.contact_details?.message}
        </p>
        <p className="ventureSupport-contact-line">
          <strong>Email:</strong> {supportData.contact_details?.email}
        </p>
        <p className="ventureSupport-contact-line">
          <strong>Phone:</strong> {supportData.contact_details?.phone}
        </p>
      </Card>
    </div>
  );
}

/* ---------------------------------------------------------------------- *
 * Local card primitive — keeps every section visually consistent.
 * ---------------------------------------------------------------------- */
function Card({ icon: Icon, title, description, children }) {
  return (
    <article className="ventureSupport-card">
      <header className="ventureSupport-card-header">
        <h2 className="ventureSupport-card-title">
          {Icon && (
            <Icon className="ventureSupport-icon-md" aria-hidden="true" />
          )}
          <span>{title}</span>
        </h2>
        {description && (
          <p className="ventureSupport-card-desc">{description}</p>
        )}
      </header>
      <div className="ventureSupport-card-body">{children}</div>
    </article>
  );
}
